import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

type Step = 1 | 2 | 3;
type AuthMethod = "oauth" | "basic";

interface TestResult {
  valid: boolean;
  connectedAs?: string;
  instanceUrl?: string;
  error?: string;
}

const SN_COLOR = "#81B5A1";

export default function ServiceNowCreate() {
  const navigate = useNavigate();
  const api = useApi();

  const [step, setStep] = useState<Step>(1);
  const [authMethod, setAuthMethod] = useState<AuthMethod>("oauth");

  // OAuth fields
  const [instanceUrl, setInstanceUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  // Basic auth fields
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Step 2
  const [displayName, setDisplayName] = useState("");
  const [pushTranscripts, setPushTranscripts] = useState(false);

  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function normalizeUrl(url: string) {
    let u = url.trim();
    if (!u.startsWith("http")) u = "https://" + u;
    return u.replace(/\/$/, "");
  }

  async function handleTest() {
    setError(null);
    setTestResult(null);

    const url = normalizeUrl(instanceUrl);
    if (!url) { setError("Enter your ServiceNow instance URL."); return; }

    if (authMethod === "oauth") {
      if (!clientId.trim()) { setError("Client ID is required."); return; }
      if (!clientSecret.trim()) { setError("Client Secret is required."); return; }
    } else {
      if (!username.trim()) { setError("Username is required."); return; }
      if (!password.trim()) { setError("Password is required."); return; }
    }

    setTesting(true);
    try {
      const resp = await api.admin.post("ServiceNow/Test", {
        instanceUrl: url,
        authMethod,
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        username: username.trim(),
        password: password.trim()
      });
      const data = await resp.json() as TestResult;
      setTestResult(data);
      if (data.valid) {
        if (!displayName) setDisplayName("ServiceNow");
        setStep(2);
      } else {
        setError(data.error || "Connection test failed. Check your credentials.");
      }
    } catch {
      setError("Failed to test connection. Please try again.");
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const resp = await api.admin.post("ServiceNow/Configs", {
        displayName: displayName.trim() || "ServiceNow",
        instanceUrl: normalizeUrl(instanceUrl),
        authMethod,
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        username: username.trim(),
        password: password.trim(),
        connectedAs: testResult?.connectedAs ?? "",
        pushTranscripts
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(body?.error ?? "Failed to save. Please try again.");
        return;
      }

      setStep(3);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  const steps = [
    { n: 1, label: "Connect" },
    { n: 2, label: "Settings" },
    { n: 3, label: "Done" }
  ];

  return (
    <div className="max-w-2xl mx-auto">
      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.n ? "text-white" : "bg-slate-200 text-slate-500"}`}
                style={step >= s.n ? { backgroundColor: SN_COLOR } : {}}
                aria-current={step === s.n ? "step" : undefined}
              >
                {step > s.n ? "✓" : s.n}
              </div>
              <span className={`text-sm ${step === s.n ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-slate-200 mx-3" aria-hidden="true" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 1: Credentials ── */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Connect your ServiceNow instance</h2>
          <p className="text-sm text-slate-500 mb-5">
            Choose your authentication method and enter your credentials. OAuth 2.0 is recommended for production environments.
          </p>

          {/* Auth method toggle */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-slate-700 mb-2">Authentication Method</label>
            <div className="flex gap-2" role="group" aria-label="Authentication method">
              <button
                type="button"
                onClick={() => setAuthMethod("oauth")}
                className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${authMethod === "oauth" ? "text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                style={authMethod === "oauth" ? { backgroundColor: SN_COLOR, borderColor: SN_COLOR } : {}}
                aria-pressed={authMethod === "oauth"}
              >
                OAuth 2.0
                <span className="ml-1.5 text-[10px] opacity-75">(recommended)</span>
              </button>
              <button
                type="button"
                onClick={() => setAuthMethod("basic")}
                className={`flex-1 py-2.5 px-4 rounded-lg border text-sm font-medium transition-colors ${authMethod === "basic" ? "text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                style={authMethod === "basic" ? { backgroundColor: SN_COLOR, borderColor: SN_COLOR } : {}}
                aria-pressed={authMethod === "basic"}
              >
                Basic Auth
              </button>
            </div>
          </div>

          {/* Instance URL — shared */}
          <label className="flex flex-col gap-1 mb-4">
            <span className="text-sm font-medium text-slate-700">
              Instance URL <span className="text-red-500" aria-hidden="true">*</span>
            </span>
            <input
              type="url"
              className="input"
              placeholder="https://mycompany.service-now.com"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
              aria-required="true"
              autoFocus
            />
            <span className="text-xs text-slate-400">Your ServiceNow instance URL — no trailing slash or path.</span>
          </label>

          {/* OAuth fields */}
          {authMethod === "oauth" && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5 text-sm text-blue-800 space-y-2">
                <p className="font-medium">How to get your OAuth credentials:</p>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>In ServiceNow, go to <strong>System OAuth → Application Registry</strong></li>
                  <li>Click <strong>New → Create an OAuth API endpoint for external clients</strong></li>
                  <li>Set Name to "Velaro Integration", leave Redirect URL blank</li>
                  <li>Save and copy the <strong>Client ID</strong> and <strong>Client Secret</strong></li>
                  <li>Ensure the service account has <code className="bg-blue-100 px-1 rounded">rest_api_explorer</code> and <code className="bg-blue-100 px-1 rounded">itil</code> roles</li>
                </ol>
                <a
                  href="https://docs.servicenow.com/bundle/vancouver-platform-security/page/administer/security/task/t_CreateEndpointforExternalClients.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline text-sm"
                >
                  View ServiceNow OAuth docs →
                </a>
              </div>

              <label className="flex flex-col gap-1 mb-4">
                <span className="text-sm font-medium text-slate-700">
                  Client ID <span className="text-red-500" aria-hidden="true">*</span>
                </span>
                <input
                  type="text"
                  className="input font-mono"
                  placeholder="Application Registry Client ID"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  aria-required="true"
                />
              </label>

              <label className="flex flex-col gap-1 mb-4">
                <span className="text-sm font-medium text-slate-700">
                  Client Secret <span className="text-red-500" aria-hidden="true">*</span>
                </span>
                <input
                  type="password"
                  className="input"
                  placeholder="Application Registry Client Secret"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  aria-required="true"
                />
              </label>
            </>
          )}

          {/* Basic auth fields */}
          {authMethod === "basic" && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-5 text-sm text-amber-800">
                <p className="font-medium mb-1">Basic Auth setup:</p>
                <p className="text-xs">Use a dedicated service account with the <strong>itil</strong> role. Do not use a personal account. For production environments, OAuth 2.0 is preferred.</p>
              </div>

              <label className="flex flex-col gap-1 mb-4">
                <span className="text-sm font-medium text-slate-700">
                  Username <span className="text-red-500" aria-hidden="true">*</span>
                </span>
                <input
                  type="text"
                  className="input"
                  placeholder="service.account@company.com"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  aria-required="true"
                />
              </label>

              <label className="flex flex-col gap-1 mb-4">
                <span className="text-sm font-medium text-slate-700">
                  Password <span className="text-red-500" aria-hidden="true">*</span>
                </span>
                <input
                  type="password"
                  className="input"
                  placeholder="Service account password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  aria-required="true"
                />
              </label>
            </>
          )}

          {error && <p className="text-red-600 text-sm mb-4" role="alert">{error}</p>}

          <div className="flex justify-end gap-2">
            <SecondaryBlueButton label="Cancel" onClick={() => navigate("/integrations/servicenow")} />
            {testing ? (
              <div className="flex items-center gap-2 text-sm text-slate-500" aria-live="polite"><LoadingSpinner /> Testing connection…</div>
            ) : (
              <button
                type="button"
                onClick={handleTest}
                className="text-sm px-4 py-2 text-white rounded-lg hover:opacity-90 font-medium"
                style={{ backgroundColor: SN_COLOR }}
              >
                Test & Continue
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: Settings ── */}
      {step === 2 && testResult?.valid && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center" aria-hidden="true">
              <span className="text-green-600 text-lg">✓</span>
            </div>
            <div>
              <div className="font-semibold text-slate-800">Connected to ServiceNow</div>
              <div className="text-sm text-slate-500">
                {normalizeUrl(instanceUrl)}
                {testResult.connectedAs ? ` · as ${testResult.connectedAs}` : ""}
              </div>
            </div>
          </div>

          <label className="flex flex-col gap-1 mb-5">
            <span className="text-sm font-medium text-slate-700">Display Name</span>
            <input
              className="input"
              placeholder="e.g. Production ServiceNow"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <span className="text-xs text-slate-400">How this connection appears in Velaro.</span>
          </label>

          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-semibold text-slate-700">Transcript Settings</h3>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={pushTranscripts}
                onChange={(e) => setPushTranscripts(e.target.checked)}
                className="mt-0.5 w-4 h-4"
              />
              <div>
                <div className="text-sm font-medium text-slate-800">Push transcripts to ServiceNow</div>
                <div className="text-xs text-slate-500">
                  When a conversation ends, automatically add a work note to the linked incident with the full chat transcript.
                </div>
              </div>
            </label>
          </div>

          {error && <p className="text-red-600 text-sm mb-4" role="alert">{error}</p>}

          <div className="flex justify-between">
            <SecondaryBlueButton label="Back" onClick={() => setStep(1)} />
            {saving ? (
              <div className="flex items-center gap-2 text-sm text-slate-500" aria-live="polite"><LoadingSpinner /> Saving…</div>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                className="text-sm px-4 py-2 text-white rounded-lg hover:opacity-90 font-medium"
                style={{ backgroundColor: SN_COLOR }}
              >
                Save & Finish
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Step 3: Done ── */}
      {step === 3 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
            <span className="text-green-500 text-3xl">✓</span>
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">ServiceNow connected!</h2>
          <p className="text-sm text-slate-500 mb-6">
            Your ServiceNow instance is now linked to Velaro. 35 AI tools are immediately available to any workflow node.
            {pushTranscripts ? " Chat transcripts will be pushed as work notes when conversations end." : ""}
          </p>
          <div className="flex justify-center gap-3">
            <SecondaryBlueButton label="Back to Integrations" onClick={() => navigate("/integrations")} />
            <button
              type="button"
              onClick={() => navigate("/integrations/servicenow")}
              className="text-sm px-4 py-2 text-white rounded-lg hover:opacity-90 font-medium"
              style={{ backgroundColor: SN_COLOR }}
            >
              Manage ServiceNow
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
